export const RADIO_FEEDBACK_REWARD_JGK = 5;
export const RADIO_FEEDBACK_MIN_SECONDS = 45;
export const RADIO_FEEDBACK_MIN_PERCENT = 35;

export const FEEDBACK_FIELDS = [
  {
    name: 'hookReaction',
    label: 'Did the hook catch you?',
    options: [
      ['caught_me_fast', 'Caught me fast'],
      ['grew_on_me', 'Grew on me'],
      ['did_not_catch_me_yet', 'Not yet'],
    ],
  },
  {
    name: 'replayValue',
    label: 'Would you run it back?',
    options: [
      ['run_it_back', 'Run it back'],
      ['maybe_later', 'Maybe later'],
      ['probably_skip', 'Probably skip'],
    ],
  },
  {
    name: 'artistIdentity',
    label: 'Is the artist identity clear?',
    options: [
      ['clear', 'Clear'],
      ['somewhat_clear', 'Somewhat'],
      ['not_clear_yet', 'Not clear yet'],
    ],
  },
  {
    name: 'fanAction',
    label: 'What action would you take?',
    options: [
      ['would_follow', 'Follow'],
      ['would_tip_support', 'Tip / support'],
      ['would_share', 'Share'],
      ['would_not_act_yet', 'Not yet'],
    ],
  },
  {
    name: 'vibeFit',
    label: 'Where does this record fit?',
    options: [
      ['radio_ready', 'Radio ready'],
      ['playlist_ready', 'Playlist ready'],
      ['club_street_energy', 'Club / street'],
      ['needs_more_polish', 'Needs polish'],
    ],
  },
];

export const STANDOUT_OPTIONS = [
  ['hook', 'Hook'],
  ['beat', 'Beat'],
  ['voice', 'Voice'],
  ['energy', 'Energy'],
  ['lyrics', 'Lyrics'],
  ['vibe', 'Vibe'],
  ['none_yet', 'None yet'],
];

export const initialFeedbackForm = {
  hookReaction: '',
  replayValue: '',
  artistIdentity: '',
  fanAction: '',
  vibeFit: '',
  standoutReason: 'none_yet',
  comment: '',
};

const toSafeSeconds = (value) => {
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
};

export const formatRadioFeedbackTime = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

export const getFeedbackListenPercent = ({ elapsed = 0, duration = 0 } = {}) => {
  const safeElapsed = toSafeSeconds(elapsed);
  const safeDuration = toSafeSeconds(duration);
  if (safeDuration <= 0) return 0;
  return Math.min(100, (safeElapsed / safeDuration) * 100);
};

export const isFeedbackUnlocked = ({ elapsed = 0, duration = 0 } = {}) =>
  toSafeSeconds(elapsed) >= RADIO_FEEDBACK_MIN_SECONDS ||
  getFeedbackListenPercent({ elapsed, duration }) >= RADIO_FEEDBACK_MIN_PERCENT;

export const getFeedbackUnlockProgress = ({
  elapsed = 0,
  duration = 0,
} = {}) =>
  Math.min(
    100,
    Math.max(
      (toSafeSeconds(elapsed) / RADIO_FEEDBACK_MIN_SECONDS) * 100,
      (getFeedbackListenPercent({ elapsed, duration }) /
        RADIO_FEEDBACK_MIN_PERCENT) *
        100,
    ),
  );

export const isFeedbackComplete = (feedbackForm = {}) =>
  FEEDBACK_FIELDS.every((field) => Boolean(feedbackForm[field.name]));

export const getRadioFeedbackTrackId = (track = {}) =>
  String(track?.id || track?.trackId || '').trim();

export const getRadioFeedbackTrackTitle = (track = {}) =>
  String(track?.songTitle || track?.title || 'Radio record').trim();

export const getRadioFeedbackArtistName = (track = {}) =>
  String(track?.artistName || 'Joe Galaxy Radio').trim();

export const getRewardMessage = (feedback = {}) => {
  const amount = Number(feedback.rewardAmountJgk);
  if (feedback.message) return feedback.message;
  if (feedback.rewardStatus === 'credited') {
    return Number.isFinite(amount) && amount > 0 ? `Feedback locked in. +${amount} JGK earned.` : 'Feedback saved. Reward credited.';
  }
  if (feedback.rewardStatus === 'already_credited') {
    return 'You already gave feedback on this record. Your signal is locked in.';
  }
  if (feedback.rewardStatus === 'pending') {
    return 'Feedback locked in. Reward pending.';
  }
  return 'Feedback could not be saved. Please try again.';
};

export const buildRadioFeedbackPayload = ({
  track,
  elapsed = 0,
  duration = 0,
  feedbackForm = initialFeedbackForm,
  source,
  route,
} = {}) => ({
  trackId: getRadioFeedbackTrackId(track),
  listenSession: {
    listenedSeconds: Math.floor(Math.max(0, Number(elapsed) || 0)),
    listenPercent: Math.round(getFeedbackListenPercent({ elapsed, duration })),
  },
  feedback: feedbackForm,
  clientMeta: {
    source,
    route,
  },
});
